import { useThemeContext } from '@huggg/react-components';
import classnames from 'classnames';
import { Legal as MiscLegal } from 'components/misc';
import PrepaidLegal from 'components/misc/PrepaidLegal/PrepaidLegal';
import {
  FadeIn as UIFadeIn,
  HugggID as UIHugggID,
  Link as UILink,
} from 'components/ui';
import dictionary from 'config/dictionary';
import { useAnalytics } from 'hooks';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { getBasePathname, isCoopVoucher } from 'utilities/huggg';
import { getFooterLink } from 'utilities/links';
import styles from './footer.module.scss';

const Footer = (props) => {
  const { operator, isWelfare } = useThemeContext();
  const { className, huggg, hasPrepaidCardVoucher, segment, showDetails } =
    props;
  const analytics = useAnalytics();
  const onTermsClick = useCallback(() => {
    analytics.buttonClickFooterTerms();
  }, [analytics]);
  const onWebsiteClick = useCallback(() => {
    analytics.outboundLinkHugggHome();
  }, [analytics]);
  const onPrivacyClick = useCallback(() => {
    analytics.outboundLinkHugggPrivacy();
  }, [analytics]);
  const onHelpClick = useCallback(() => {
    analytics.outboundLinkHugggHelp();
  }, [analytics]);

  const isCoop = isCoopVoucher(huggg);

  const website = getFooterLink(segment, 'website');
  const partnerTerms = getFooterLink(segment, 'partnerTerms');
  const privacyPolicy = getFooterLink(segment, 'privacyPolicy');
  const help = getFooterLink(segment, 'help');

  const configuredLink = (link, onClick) => (
    <UILink.BaseLink
      className={styles.linkListLink}
      href={link.href}
      key={null}
      newWindow={true}
      onClick={onClick}
    >
      {link.label}
    </UILink.BaseLink>
  );

  /**
   * Links to display in the footer. eslint moans if we don't add a key prop,
   * but we don't actually need a key prop. Silly eslint.
   */
  const links = [
    website.render &&
      (website.link ? (
        configuredLink(website.link, onWebsiteClick)
      ) : (
        <UILink.HugggHome className={styles.linkListLink} key={null} />
      )),
    partnerTerms.render &&
      (partnerTerms.link
        ? configuredLink(partnerTerms.link, onTermsClick)
        : huggg.id && (
            <RouterLink
              className={styles.linkListLink}
              key={null}
              onClick={onTermsClick}
              to={`${getBasePathname(huggg)}/partner-terms`}
            >
              {isCoop
                ? dictionary.footer.links.terms
                : dictionary.footer.links.partnerTerms}
            </RouterLink>
          )),
    privacyPolicy.render &&
      (privacyPolicy.link ? (
        configuredLink(privacyPolicy.link, onPrivacyClick)
      ) : (
        <UILink.HugggPrivacy className={styles.linkListLink} key={null} />
      )),
    help.render &&
      (help.link
        ? configuredLink(help.link, onHelpClick)
        : operator.showHugggSupportLinks && (
            <UILink.HugggHelp
              className={styles.linkListLink}
              key={null}
              isWelfare={isWelfare}
            />
          )),
  ].filter((_) => _);

  return (
    <footer
      className={classnames(styles.footer, className)}
      data-test-id="footer"
    >
      <div className="container">
        <UIFadeIn mountOnEnter={true} isVisible={showDetails}>
          <nav
            className={classnames(styles.footerNav, 'u-font-smallest')}
            data-test-id="footer-navigation"
          >
            <ul className={styles.linkList}>
              {links.map((link, index) => (
                <li className={styles.linkListItem} key={index}>
                  {link}
                </li>
              ))}
            </ul>
          </nav>
        </UIFadeIn>
      </div>

      <UIFadeIn mountOnEnter={true} isVisible={showDetails}>
        <div className="container container--wide">
          {hasPrepaidCardVoucher && (
            <PrepaidLegal
              className={classnames(styles.legal, 'u-font-smallest')}
            />
          )}
          <MiscLegal className={classnames(styles.legal, 'u-font-smallest')} />
          <UIHugggID className={styles.hugggID} id={huggg.id} />
        </div>
      </UIFadeIn>
    </footer>
  );
};

Footer.propTypes = {
  huggg: PropTypes.object.isRequired,
  hasPrepaidCardVoucher: PropTypes.bool,
  segment: PropTypes.object,
  className: PropTypes.string,
  showDetails: PropTypes.bool,
};

export default Footer;
